/**
 * Gateway — CBSTransactionsHistoryGateway
 *
 * Récupère l'historique des transactions du caissier connecté depuis le CBS.
 *
 * Endpoint : POST /api/v1/cbs-integration/users/transactions-history
 * Payload  : { "codeuser": "<codeUser>" }
 *
 * Le CBS retourne directement les noms de clients (champ `intitule`),
 * donc aucun appel d'enrichissement supplémentaire n'est nécessaire.
 */

import { buildBearerAuthHeader } from '../http/auth';
import type { HistoryTransaction } from './CardLifeHistoryGateway';

const ENV = ((import.meta as ImportMeta & { env?: Record<string, string | undefined> }).env ?? {});
const BASE_URL = (ENV.VITE_CARDLIFE_BASE_URL ?? '').replace(/\/+$/g, '');
const TRANSACTIONS_HISTORY_ENDPOINT = '/api/v1/cbs-integration/users/transactions-history';

// ─────────────────────────────────────────────
// Types réponse CBS
// ─────────────────────────────────────────────

export interface CBSTransactionItem {
  reference: string;
  date: string;       // Format DD/MM/YY
  montant: string;    // Signé : négatif = versement, positif = retrait
  devise: string;
  operation: string;  // "Versement espèc", "Retrait espèces", "Alimentation d'", etc.
  compte: string;
  intitule: string;   // Nom du titulaire du compte
  motif: string;
}

export interface CBSTransactionsHistoryResponse {
  response: string;            // "0" = succès
  codeuser: string;
  message?: string;
  transactions: CBSTransactionItem[];
}

// ─────────────────────────────────────────────
// Utilitaires
// ─────────────────────────────────────────────

/**
 * Convertit "DD/MM/YY" en Date.
 * Hypothèse : YY < 100 → 2000 + YY (transactions récentes).
 */
function parseCBSDate(ddMMyy: string | undefined): Date {
  if (!ddMMyy) return new Date();
  const parts = ddMMyy.trim().split('/');
  if (parts.length !== 3) return new Date();
  const [dd, mm, yy] = parts;
  const year = yy.length === 2 ? 2000 + parseInt(yy, 10) : parseInt(yy, 10);
  return new Date(year, parseInt(mm, 10) - 1, parseInt(dd, 10));
}

/**
 * Détermine le type de transaction à partir du libellé `operation` (prioritaire)
 * puis du signe du montant (fallback).
 */
function detectType(operation: string, montant: number): 'versement' | 'retrait' {
  const op = (operation ?? '').toLowerCase();
  if (op.startsWith('versement')) return 'versement';
  if (op.startsWith('retrait')) return 'retrait';
  // Fallback sur le signe : convention CBS — négatif = entrée (versement), positif = sortie (retrait)
  return montant < 0 ? 'versement' : 'retrait';
}

/**
 * Hash simple d'une chaîne pour générer un operationId numérique stable.
 */
function hashRef(ref: string): number {
  let h = 0;
  for (let i = 0; i < ref.length; i++) {
    h = ((h << 5) - h) + ref.charCodeAt(i);
    h |= 0;
  }
  return Math.abs(h);
}

// ─────────────────────────────────────────────
// Gateway
// ─────────────────────────────────────────────

export class CBSTransactionsHistoryGateway {

  /**
   * Récupère l'historique CBS d'un caissier et le mappe vers HistoryTransaction[].
   *
   * @param codeuser - Code agent / caissier (ex: "1080W")
   * @returns Transactions triées par date décroissante
   */
  async fetchHistory(codeuser: string): Promise<HistoryTransaction[]> {
    if (!codeuser || codeuser.trim() === '') {
      throw new Error('Code agent requis pour récupérer l\'historique CBS');
    }

    const url = `${BASE_URL}${TRANSACTIONS_HISTORY_ENDPOINT}`;
    const authHeader = buildBearerAuthHeader();

    const response = await fetch(url, {
      method: 'POST',
      credentials: 'include',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        ...(authHeader ? { Authorization: authHeader } : {}),
      },
      body: JSON.stringify({ codeuser: codeuser.trim() }),
    });

    if (!response.ok) {
      const text = await response.text();
      console.error('[CBSHistory] Erreur HTTP:', response.status, text);
      throw new Error(`Erreur CBS historique: ${response.status} - ${text}`);
    }

    // Le backend renvoie le JSON brut du CBS (peut être objet ou string JSON)
    const contentType = response.headers.get('content-type') ?? '';
    const raw = contentType.includes('application/json')
      ? await response.json()
      : JSON.parse(await response.text());

    const data: CBSTransactionsHistoryResponse =
      typeof raw === 'string' ? JSON.parse(raw) : raw;

    if (data.response !== undefined && data.response !== '0') {
      console.warn('[CBSHistory] Erreur CBS:', data.message);
      throw new Error(data.message || 'Erreur lors de la récupération de l\'historique CBS');
    }

    const items = Array.isArray(data.transactions) ? data.transactions : [];
    console.log(`[CBSHistory] ${items.length} transactions reçues du CBS pour ${codeuser}`);

    const transactions = items.map((item) => this.mapItem(item, codeuser));
    transactions.sort((a, b) => b.date.getTime() - a.date.getTime());
    return transactions;
  }

  private mapItem(item: CBSTransactionItem, codeuser: string): HistoryTransaction {
    const montant = parseInt(item.montant, 10) || 0;
    const type = detectType(item.operation, montant);

    return {
      id: `CBS-${item.reference}`,
      type,
      amount: Math.abs(montant),
      currency: item.devise || 'XOF',
      accountNumber: item.compte || '',
      reference: item.reference || '',
      date: parseCBSDate(item.date),
      cashier: codeuser,
      status: 'completed',
      motif: item.motif || item.operation || '',
      isTiers: false,
      deposantNom: '',
      operationId: hashRef(item.reference || ''),
      clientName: item.intitule || '',
    };
  }
}
